import ContactsIcon from '@mui/icons-material/Contacts';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolderOutlined';
import FormIcon from '@mui/icons-material/ListAltOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Access, { ACTIONS, MODULES } from 'components/Access';
import Button from 'components/Button/Button';
import DataTable, { TableActions, TableContainer } from 'components/DataTable';
import TableRow from 'components/DataTable/TableRow';
import Dropdown from 'components/Dropdown';
import EditableTags from 'components/EditableTags';
import IconButton from 'components/IconButton/IconButton';
import PageWrapper from 'components/PageWrapper';
import Tabs from 'components/Tabs/Tabs';
import Folder from 'components/categories/Folder';
import MoveToFolderDialog from 'components/categories/MoveToCategory';
import useFolderData from 'components/categories/useFolderData';
import TextInput from 'components/form/TextInput';
import useAlert from 'hooks/useAlert';
import useQueryParams from 'hooks/useQueryParams';
import useRequest from 'hooks/useRequest';
import escapeRegExp from 'lodash/escapeRegExp';
import orderBy from 'lodash/orderBy';
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  deleteCategory,
  getCategories,
  saveCategory,
} from 'services/categories.service';
import {
  deleteConnectCard,
  duplicateConnectCard,
  getConnectCards,
  saveConnectCard,
} from 'services/connect-card.service';
import { getConnectCardTags } from 'services/tags.service';
import toast from 'utils/toast';
import Report from '../components/connect-card/ConnectCardReport';
import CreateModal from '../components/connect-card/CreateModal';
import { getCardCompletionRate } from '../utils';

const filter = (cards, query) => {
  const regx = new RegExp(escapeRegExp(query), 'i');
  const r = [];
  cards.forEach((card) => {
    if (card.isFolder) {
      const c = { ...card };
      c.subItems = filter(c.subItems, query);
      if (c.subItems.length) {
        r.push(c);
      }
    } else {
      const tagMatch = Array.isArray(card.tags)
        ? card.tags.some((t) => {
            const label = typeof t === 'string' ? t : t.tag || '';
            return regx.test(label);
          })
        : false;
      if (regx.test(card.card_name) || tagMatch) {
        r.push(card);
      }
    }
  });
  return r;
};

const ConnectCards = ({ location }) => {
  const [tab, setTab] = useState('active');
  const alert = useAlert();
  const params = useQueryParams();
  const [showCreateModal, setShowCreateModal] = useState(
    params.action === 'create',
  );
  const account = useSelector((s) => s.settings.account);
  const [selectedCard, setSelectedCard] = useState(null);
  const [movePrams, setMoveParams] = useState();
  const [expandedRows, setExpandedRows] = useState({});
  const [cards, { loading, refetch, error, updateCache }] = useRequest(
    getConnectCards,
    {
      account_id: account.account_id,
      load_group: 1,
      load_tags: 1,
      card_deleted: tab === 'active' ? 0 : 1,
    },
  );
  const [folders, { loading: loadingCategories, refetch: refetchFolders }] =
    useRequest(getCategories, {
      type: 'connect_card',
      load_category_items: 1,
    });
  const [sort, setSort] = useState({
    sortField: 'card_name',
    sortOrder: 'asc',
  });
  const [query, setQuery] = useState('');

  const processedData = useMemo(() => {
    return cards?.map((card) => {
      return {
        ...card,
        group_name: card.group?.group_name,
        card_visits: +card.card_visits,
        card_completions: +card.card_completions,
        card_completion_rate: getCardCompletionRate(card),
      };
    });
  }, [cards]);

  const data = useFolderData(folders, processedData, (i) => i.id);

  const handleMenuClick = async (action, data) => {
    if (data.isFolder) {
      switch (action) {
        case 'edit': {
          alert.prompt({
            title: 'Edit Folder',
            description: 'Folder Name',
            confirmationText: 'Save',
            inputValue: data.category_name,
            onConfirm: async (val) => {
              try {
                await saveCategory({
                  hash: data.hash,
                  category_name: val,
                });
                return refetchFolders();
              } catch (e) {
                toast.error(e.message);
              }
            },
          });
          break;
        }
        case 'delete': {
          alert.confirm({
            severity: 'warning',
            description: 'Are you sure you want to delete this folder?',
            onConfirm: async () => {
              try {
                await deleteCategory(data.hash);
                return refetchFolders();
              } catch (e) {
                toast.error(e.message);
              }
            },
          });
          break;
        }
      }
    } else {
      switch (action) {
        case 'duplicate':
          duplicateConnectCard(data.hash)
            .then(() => {
              refetch();
              toast.success('Connect card duplicated.');
            })
            .catch((e) => toast.error(e.message));
          break;
        case 'delete':
          alert.confirm({
            severity: 'warning',
            description: 'Are you sure you want to delete this Connect Card?',
            onConfirm: async () => {
              try {
                await deleteConnectCard(data.hash);
                updateCache((cards) =>
                  cards.filter((card) => card.hash !== data.hash),
                );
                toast.success('Connect card deleted.');
              } catch (e) {
                toast.error(e.message);
              }
            },
          });
          break;
        case 'move': {
          onMoveCategory(data);
          break;
        }
        default:
          break;
      }
    }
  };

  const onRestore = (row) => {
    return saveConnectCard({
      card_deleted: 0,
      hash: row.hash,
    })
      .then(() => {
        updateCache((cards) => cards.filter((card) => card.hash !== row.hash));
        toast.success('Connect card restored.');
      })
      .catch((e) => toast.error(e.message));
  };

  const onAddCC = () => {
    setShowCreateModal(true);
  };

  const onAddFolder = () => {
    alert.prompt({
      dialogSize: 'xs',
      title: 'Create Folder',
      description: 'Folder name',
      onConfirm: async (value) => {
        try {
          await saveCategory({
            category_name: value,
            object_type: 'connect_card',
          });
          return refetchFolders();
        } catch (e) {
          toast.error(e.message);
        }
      },
      confirmationText: 'Create',
    });
  };

  const onMoveCategory = (item) => {
    setMoveParams({
      item,
    });
  };

  const onTagsUpdate = (cardHash, updatedTags) => {
    updateCache((prev) =>
      prev.map((card) =>
        card.hash === cardHash
          ? { ...card, tags: updatedTags }
          : card
      )
    );
  };

  const onToggleExpand = (row) => {
    expandedRows[row.hash] = !expandedRows[row.hash];
    setExpandedRows({ ...expandedRows });
  };

  const onTableChange = (type, props) => {
    if (type === 'sort') {
      setSort(props);
    }
  };

  const columns = [
    {
      text: 'Name',
      dataField: 'card_name',
      sortable: true,
      formatter: (val, row) => {
        if (row.isFolder) {
          return (
            <Folder
              onClick={() => onToggleExpand(row)}
              isExpanded={expandedRows[row.hash]}
              count={row.subItems.length}
              label={row.category_name}
            />
          );
        }
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            {row.category_item_hash && (
              <Box sx={{ pl: 1 }}>
                <SubdirectoryArrowRightIcon fontSize="small" color="action" />
              </Box>
            )}
            <ContactsIcon color="primary" />
            <span>{val}</span>
          </Stack>
        );
      },
    },
    {
      text: 'Type',
      dataField: 'card_type',
      sortable: true,
      formatter: (val) => {
        if (val) {
          return (
            <Chip
              color={val === 'standard' ? 'primary' : 'info'}
              label={val === 'standard' ? 'Standard Card' : 'Multi-Step Card'}
              variant="outlined"
              size="small"
            />
          );
        }
      },
    },
    {
      dataField: 'group_name',
      text: 'Linked Group',
      sortable: true,
    },
    {
      dataField: 'card_visits',
      sortable: true,
      align: 'center',
      text: 'Visits',
    },
    {
      dataField: 'card_completions',
      sortable: true,
      align: 'center',
      text: 'Completed',
    },
    {
      dataField: 'card_completion_rate',
      sortable: true,
      align: 'center',
      text: '% Completed',
    },
    {
      dataField: 'report',
      align: 'center',
      text: 'Report',
      formatter: (cell, row) => {
        if (row.isFolder) return;
        return (
          <Button
            variant="text"
            startIcon={<FormIcon />}
            onClick={() => setSelectedCard(row)}
          >
            Report
          </Button>
        );
      },
    },
    {
      text: 'Tags',
      dataField: 'tags',
      align: 'left',
      sortable: false,
      formatter: (val, row) => {
        if (row.isFolder) return null;
        return (
          <Access action={ACTIONS.MODIFY} module={MODULES.CONNECT_CARDS}>
            <EditableTags
              row={row}
              onTagsUpdate={onTagsUpdate}
              objectType="connect_card"
              getObjectTags={() => val.tags}
              getObjectId={(row) => row.id}
              getObjectHash={(row) => row.hash}
            />
          </Access>
        );
      },
    },
    {
      dataField: 'hash',
      text: 'Actions',
      align: 'right',
      formatter: (cell, row) => {
        if (row.card_deleted === '1')
          return (
            <Access module={MODULES.CONNECT_CARDS} action={ACTIONS.MODIFY}>
              <Button variant="text" onClick={() => onRestore(row)}>
                Restore
              </Button>
            </Access>
          );

        const actions = row.isFolder
          ? [
              { key: 'edit', label: 'Edit' },
              { key: 'delete', label: 'Delete', destructive: true },
            ]
          : [
              {
                key: 'edit',
                children: 'Edit',
                component: Link,
                to: `/connect/cards/${cell}`,
              },
              { key: 'duplicate', children: 'Duplicate' },
              { key: 'move', children: 'Move To' },
              { key: 'delete', children: 'Delete', destructive: true },
            ];
        return (
          <Access action={ACTIONS.MODIFY} module={MODULES.CONNECT_CARDS}>
            <Dropdown
              onSelect={(v) => handleMenuClick(v, row)}
              id="actions"
              options={actions}
            >
              <IconButton size="mini" icon={<MoreHorizIcon />} />
            </Dropdown>
          </Access>
        );
      },
    },
  ];

  const tableData = useMemo(() => {
    const { sortField, sortOrder } = sort;
    if (!data) {
      return [];
    }
    const d = tab === 'active' ? data : processedData;
    const filtered = query ? filter(d, query) : d;
    const s = orderBy(
      filtered,
      [
        'isFolder',
        (i) => {
          return sortField === 'card_name'
            ? (i.isFolder ? i.category_name : i.card_name).toLowerCase()
            : i[sortField];
        },
      ],
      ['asc', sortOrder],
    );
    s.forEach((cat) => {
      if (cat.subItems) {
        cat.subItems = orderBy(cat.subItems, [sortField], [sortOrder]);
      }
    });
    return s;
  }, [sort, data, query, processedData]);

  const renderChildRows = (row) => {
    if (!row.isFolder) return undefined;
    if (!expandedRows[row.hash]) return false;
    return row.subItems.map((subItem, idx) => {
      return (
        <TableRow
          key={subItem.id}
          row={subItem}
          columns={columns}
          keyField="id"
          index={idx}
        />
      );
    });
  };

  return (
    <PageWrapper
      title="Connect Cards"
      breadcrumbs={[{ title: 'Connect Cards' }]}
      error={error}
      actions={
        <Access action={ACTIONS.MODIFY} module={MODULES.CONNECT_CARDS}>
          <Button
            onClick={onAddFolder}
            variant="outlined"
            startIcon={<CreateNewFolderIcon />}
          >
            New Folder
          </Button>
          <Button onClick={onAddCC}>Add New</Button>
        </Access>
      }
    >
      <Helmet title="Connect Cards" />
      <TableContainer>
        <Tabs
          tabs={[
            { label: 'Active', value: 'active' },
            { label: 'Deleted', value: 'deleted' },
          ]}
          value={tab}
          onChange={setTab}
        >
          <TableActions>
            <TextInput
              placeholder="Search..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              margin="none"
              size="small"
              startAdornment={<SearchIcon />}
              sx={{ maxWidth: 300 }}
            />
          </TableActions>
          <DataTable
            pagination
            loading={loading}
            keyField="hash"
            size="small"
            columns={columns}
            data={tableData}
            onTableChange={onTableChange}
            renderChild={renderChildRows}
          />
        </Tabs>
      </TableContainer>
      {selectedCard && (
        <Report data={selectedCard} onClose={() => setSelectedCard(null)} />
      )}
      {showCreateModal && (
        <CreateModal onClose={() => setShowCreateModal(false)} />
      )}
      {movePrams && (
        <MoveToFolderDialog
          onClose={() => setMoveParams(null)}
          folders={folders}
          itemId={movePrams.item.id}
          existingCategoryId={movePrams.item.category_id}
          existingCategoryItemHash={movePrams.item.category_item_hash}
          getCategories={refetchFolders}
        />
      )}
    </PageWrapper>
  );
};

export default ConnectCards;
